import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
 ArrowRight, Phone, MapPin, Users, Anchor, Star, Plus, Minus,
 ChefHat, Music, Ship, GlassWater, Sparkles, Clock,
 Cake, Briefcase, Heart, PartyPopper, Crown, Rocket, Network, Gift
} from "lucide-react";
import { CONTACT, GALLERY, TESTIMONIALS } from "../data/cruiseData";
import EnquiryForm from "../components/EnquiryForm";
import SEOHead from "../components/SEOHead";

// Real Magic harbour assets
const HERO = "https://customer-assets.emergentagent.com/job_magic-harbour-party/artifacts/wyn0mqkq_cruise-sydney-harbour.jpg";
const ABOUT_IMG = "https://customer-assets.emergentagent.com/job_magic-harbour-party/artifacts/4tjapa4l_cruise-boat-sydney-harbour.jpg";

const EVENT_CARDS = [
 { slug: "birthday-cruises-sydney-harbour", label: "Birthday Cruises", icon: Cake, accent: "from-pink-500 to-fuchsia-500" },
 { slug: "corporate-cruises-sydney-harbour", label: "Corporate Cruises", icon: Briefcase, accent: "from-sky-500 to-blue-600" },
 { slug: "wedding-cruises-sydney-harbour", label: "Wedding Cruises", icon: Heart, accent: "from-rose-500 to-pink-600" },
 { slug: "boat-party-sydney-harbour", label: "Party Boat Hire", icon: PartyPopper, accent: "from-amber-500 to-orange-600" },
 { slug: "hens-party-cruises-sydney-harbour", label: "Hens Parties", icon: Crown, accent: "from-fuchsia-500 to-rose-500" },
 { slug: "networking-event-cruises-sydney", label: "Networking Events", icon: Network, accent: "from-violet-500 to-indigo-600" },
 { slug: "product-launch-cruises-sydney", label: "Product Launches", icon: Gift, accent: "from-cyan-500 to-sky-600" }];

const FEATURES = [
 { label: "Catering & Drinks", Icon: ChefHat, copy: "Crystal · Emerald · Diamond menus" },
 { label: "DJs & Entertainment", Icon: Music, copy: "Pro DJ from $550 · live bands available" },
 { label: "Multiple Pickup Wharves", Icon: MapPin, copy: "King St Wharf · Circular Quay · Barangaroo" },
 { label: "Flexible Packages", Icon: Sparkles, copy: "Canapé · buffet · BYO · seated dining" },
 { label: "Private Charter", Icon: Anchor, copy: "Exclusive vessel · no shared sailings" },
 { label: "Sydney Harbour Views", Icon: Ship, copy: "Opera House · Bridge · Fort Denison" }];

const PACKAGES = [
 { label: "Birthday Packages", from: "from $4,000", desc: "4hr cruise · canapé / buffet · dance floor · cake table", slug: "birthday-cruises-sydney-harbour", accent: "fuchsia" },
 { label: "Corporate Packages", from: "from $4,000", desc: "3hr exclusive hire · canapé · standing buffet · AV ready", slug: "corporate-cruises-sydney-harbour", accent: "blue" },
 { label: "Private Charter", from: "from $2,000", desc: "3 to 4hr exclusive · 120 guests · full bar · onboard chef", slug: null, accent: "amber" }];

const PACKAGE_THEME = {
 fuchsia: { bg: "bg-fuchsia-50", text: "text-fuchsia-700", ring: "ring-fuchsia-200", grad: "from-fuchsia-500 to-pink-600" },
 blue: { bg: "bg-sky-50", text: "text-sky-700", ring: "ring-sky-200", grad: "from-sky-500 to-blue-600" },
 amber: { bg: "bg-amber-50", text: "text-amber-700", ring: "ring-amber-200", grad: "from-amber-500 to-orange-600" },
};

const FAQS = [
 { q: "How many guests can the boat hold?", a: "The MV Sydney Magic is licensed for up to 120 guests across two decks. Seated dining accommodates 40, standing canapé service comfortably hosts 95, and cocktail-style charters run up to the full 120." },
 { q: "Can we bring our own DJ?", a: "Yes, bring your own DJ, photographer, celebrant, decorator or band. We also offer in-house add-ons: Pro DJ ($550), Photographer ($650), Magicians ($1,800). The boat has a built-in DJ-ready sound system." },
 { q: "What catering options are available?", a: "Three menu tiers: Crystal Buffet ($65pp), Emerald Buffet ($85pp), Diamond Buffet ($95pp). Plus canapé menus from $30pp, beverage packages at $15.50pp/hr, and full BYO options on selected dates." },
 { q: "Where do cruises depart from?", a: "All charters depart from King Street Wharf, Darling Harbour. Optional pickups at Circular Quay, Barangaroo, Pyrmont, Campbells Cove and Walsh Bay are available for +$55 per wharf." },
 { q: "Can we decorate the boat?", a: "Absolutely, balloon installs, marquee numbers, photo backdrops, branded signage, table styling, the lot. Our crew helps set up before guests board. Anything non-permanent is welcome." }];

const GALLERY_GRID = [
 GALLERY[0], GALLERY[5], GALLERY[3],
 GALLERY[7], GALLERY[2], GALLERY[15],
 GALLERY[12], GALLERY[16], GALLERY[14]];

export default function PrivateBoatHire() {
 const [openFaq, setOpenFaq] = useState(0);

 useEffect(() => {
  document.title = "Private Boat Hire Sydney Harbour | Magic Cruises";
 }, []);

 return (
 <main className="pt-20 bg-[hsl(45,100%,98%)]" data-testid="private-boat-hire-page">
  <SEOHead
   title="Private Boat Hire Sydney Harbour | Magic Cruises"
   description="Private Sydney Harbour cruise charters for birthdays, corporate events, weddings, networking & celebrations onboard The Magic. Up to 120 guests · King St Wharf."
   image={HERO}
   type="Event"
   schemaExtra={{
    keywords: "private boat hire Sydney Harbour, Sydney Harbour charters, party boat hire Sydney, private harbour cruises, corporate cruises Sydney",
    location: { "@type": "Place", name: "Sydney Harbour · MV Sydney Magic", address: "King Street Wharf, Darling Harbour, Sydney NSW" },
   }}
  />

  {/* ───── 1. HERO ───── */}
  <section className="relative overflow-hidden" data-testid="pbh-hero">
   <div className="absolute inset-0">
    <img src={HERO} alt="Private boat hire Sydney Harbour · MV Sydney Magic at sunset under the Bridge" className="w-full h-full object-cover" />
    <div className="absolute inset-0 bg-gradient-to-b from-[hsla(222,40%,6%,0.78)] via-[hsla(222,40%,8%,0.62)] to-[hsla(222,40%,8%,0.92)]" />
    <div className="absolute inset-0 bg-gradient-to-r from-[hsla(222,40%,6%,0.65)] via-[hsla(222,40%,6%,0.15)] to-transparent" />
   </div>
   <div className="relative max-w-7xl mx-auto px-5 lg:px-10 py-24 lg:py-36">
    <div className="max-w-3xl">
     <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-amber-400 text-[hsl(225,75%,12%)] text-[10px] uppercase tracking-[0.28em] font-black mb-6">
      <Anchor className="w-3 h-3" /> Private Charter · From $2,000
     </div>
     <h1 className="font-display text-white font-black tracking-tight leading-[0.95] drop-shadow-[0_4px_24px_rgba(0,0,0,0.7)]" style={{ fontSize: "clamp(2.5rem, 7vw, 5.5rem)", letterSpacing: "-0.025em" }} data-testid="pbh-h1">
      Private Boat Hire <span className="italic font-light text-amber-300">Sydney Harbour.</span>
     </h1>
     <p className="text-white/85 text-base lg:text-xl mt-6 max-w-2xl leading-relaxed font-light">
      Private Sydney Harbour cruise charters for birthdays, corporate events, weddings, networking functions and unforgettable celebrations onboard The Magic.
     </p>
     <div className="flex flex-wrap gap-3 mt-8">
      <a href="#enquiry" className="inline-flex items-center gap-2 px-7 py-4 rounded-full bg-amber-400 hover:bg-amber-300 text-[hsl(225,75%,12%)] font-black text-sm tracking-[0.18em] uppercase shadow-2xl shadow-amber-500/40 transition-all" data-testid="pbh-cta-quote">
       Get a quote <ArrowRight className="w-4 h-4" />
      </a>
      <a href="#packages" className="inline-flex items-center gap-2 px-7 py-4 rounded-full bg-white/10 backdrop-blur ring-2 ring-white/40 hover:bg-white/20 text-white font-black text-sm tracking-[0.18em] uppercase transition-all" data-testid="pbh-cta-packages">
       View packages <ArrowRight className="w-4 h-4" />
      </a>
     </div>
     <div className="flex flex-wrap gap-x-7 gap-y-2 mt-10 text-white/80 text-xs lg:text-sm font-bold">
      <span className="inline-flex items-center gap-2"><Users className="w-4 h-4 text-amber-300" /> Up to 120 guests</span>
      <span className="inline-flex items-center gap-2"><Anchor className="w-4 h-4 text-amber-300" /> Sydney Harbour</span>
      <span className="inline-flex items-center gap-2"><Sparkles className="w-4 h-4 text-amber-300" /> Private charters</span>
      <span className="inline-flex items-center gap-2"><ChefHat className="w-4 h-4 text-amber-300" /> Catering &amp; entertainment</span>
     </div>
    </div>
   </div>
  </section>

  {/* ───── 2. QUICK EVENT NAVIGATION ───── */}
  <section className="relative py-16 lg:py-20" data-testid="pbh-event-nav">
   <div className="max-w-7xl mx-auto px-5 lg:px-10">
    <div className="flex items-end justify-between gap-6 mb-8">
     <div>
      <div className="text-pink text-[11px] uppercase tracking-[0.32em] font-black mb-2">What are you celebrating?</div>
      <h2 className="font-display text-3xl lg:text-5xl text-ink font-black tracking-tight leading-[1.05]">Charters for every <span className="italic font-light text-pink">occasion.</span></h2>
     </div>
     <Link to="/cruises" className="hidden sm:inline-flex items-center gap-1.5 text-pink font-black text-xs uppercase tracking-[0.18em] hover:underline">See all <ArrowRight className="w-3.5 h-3.5" /></Link>
    </div>
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 lg:gap-4">
     {EVENT_CARDS.map((c) => {
      const Ico = c.icon;
      return (
       <Link key={c.slug} to={`/cruises/${c.slug}`} className="group relative bg-white rounded-2xl p-4 lg:p-5 ring-1 ring-[hsla(222,20%,80%,0.6)] hover:ring-pink hover:-translate-y-1 hover:shadow-xl transition-all overflow-hidden" data-testid={`pbh-event-${c.slug}`}>
        <div className={`w-11 h-11 rounded-xl bg-gradient-to-br ${c.accent} text-white flex items-center justify-center shadow-lg mb-3 group-hover:scale-110 transition-transform`}>
         <Ico className="w-5 h-5" />
        </div>
        <div className="font-display text-sm lg:text-base font-black tracking-tight text-ink leading-tight">{c.label}</div>
        <div className="text-pink text-[10px] font-black uppercase tracking-[0.18em] mt-2 inline-flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">Explore <ArrowRight className="w-3 h-3" /></div>
       </Link>
      );
     })}
    </div>
   </div>
  </section>

  {/* ───── 3. ABOUT / SEO SPLIT ───── */}
  <section className="relative py-16 lg:py-24 bg-white" data-testid="pbh-about">
   <div className="max-w-7xl mx-auto px-5 lg:px-10">
    <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
     <div className="lg:col-span-7">
      <div className="text-pink text-[11px] uppercase tracking-[0.32em] font-black mb-3">About the Magic</div>
      <h2 className="font-display text-3xl lg:text-5xl text-ink font-black tracking-tight leading-[1.02]">
       The MV Sydney Magic. <span className="italic font-light text-pink">Yours for the day or night.</span>
      </h2>
      <div className="space-y-4 mt-6 text-ink-soft text-[15px] lg:text-[16px] leading-[1.75] max-w-2xl">
       <p>
        <strong className="text-ink">Private boat hire Sydney</strong> done properly, exclusive use of a 120-capacity 2-deck Sydney Harbour cruiser, fully licensed bar, professional crew and onboard chef. No shared sailings, no curfews, no compromises.
       </p>
       <p>
        From birthdays and weddings to corporate cruises Sydney teams actually look forward to, the Magic shapes around your event. Choose your menu, your bar package, your music, your decor, your route. We bring the boat, the crew and the harbour.
       </p>
      </div>
      <ul className="grid sm:grid-cols-2 gap-3 mt-7 text-sm font-bold text-ink">
       {[
        "Private exclusive use", "120 guests · two decks", "Fully licensed onboard bar", "From $2,000 · 3hr charter", "King St Wharf boarding", "DJ-ready sound system"].map((b, i) => (
        <li key={i} className="flex items-center gap-2.5">
         <span className="w-5 h-5 rounded-md bg-pink/10 text-pink flex items-center justify-center shrink-0"><Sparkles className="w-3 h-3" /></span>
         {b}
        </li>
       ))}
      </ul>
     </div>
     <div className="lg:col-span-5">
      <div className="relative rounded-3xl overflow-hidden shadow-[0_40px_80px_-30px_rgba(15,27,61,0.4)] aspect-[4/5]">
       <img src={ABOUT_IMG} alt="Inside the MV Sydney Magic saloon · private boat hire Sydney Harbour" loading="lazy" className="absolute inset-0 w-full h-full object-cover" />
       <div className="absolute bottom-5 left-5 right-5">
        <div className="inline-flex items-center gap-1.5 bg-amber-400 text-[hsl(225,75%,12%)] px-3 py-1.5 rounded-full text-[10px] uppercase tracking-[0.22em] font-black shadow-lg">
         <Star className="w-3 h-3" fill="currentColor" strokeWidth={0} /> 5★ · 2,500+ reviews
        </div>
       </div>
      </div>
     </div>
    </div>
   </div>
  </section>

  {/* ───── 4. EXPERIENCE FEATURES (compact icon row) ───── */}
  <section className="relative py-16 lg:py-20" data-testid="pbh-features">
   <div className="max-w-7xl mx-auto px-5 lg:px-10">
    <div className="max-w-2xl mb-10">
     <div className="text-pink text-[11px] uppercase tracking-[0.32em] font-black mb-2">The experience</div>
     <h2 className="font-display text-3xl lg:text-4xl text-ink font-black tracking-tight leading-[1.05]">Everything <span className="italic font-light text-pink">included.</span></h2>
    </div>
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 lg:gap-4">
     {FEATURES.map((f, i) => {
      const Ico = f.Icon;
      return (
       <div key={i} className="bg-white rounded-2xl p-4 lg:p-5 ring-1 ring-[hsla(222,20%,80%,0.55)] hover:ring-pink/40 hover:shadow-md transition-all" data-testid={`pbh-feature-${i}`}>
        <div className="w-10 h-10 rounded-xl bg-pink/10 text-pink flex items-center justify-center mb-3">
         <Ico className="w-5 h-5" strokeWidth={2} />
        </div>
        <div className="font-display text-sm font-black tracking-tight text-ink leading-tight">{f.label}</div>
        <div className="text-[11px] text-ink-soft mt-1.5 leading-snug">{f.copy}</div>
       </div>
      );
     })}
    </div>
   </div>
  </section>

  {/* ───── 5. GALLERY ───── */}
  <section className="relative py-16 lg:py-20 bg-white" data-testid="pbh-gallery">
   <div className="max-w-7xl mx-auto px-5 lg:px-10">
    <div className="flex items-end justify-between gap-6 mb-8">
     <div>
      <div className="text-pink text-[11px] uppercase tracking-[0.32em] font-black mb-2">On board the Magic</div>
      <h2 className="font-display text-3xl lg:text-4xl text-ink font-black tracking-tight leading-[1.05]">Real cruises. <span className="italic font-light text-pink">Real moments.</span></h2>
     </div>
     <Link to="/gallery" className="hidden sm:inline-flex items-center gap-1.5 text-pink font-black text-xs uppercase tracking-[0.18em] hover:underline">Full gallery <ArrowRight className="w-3.5 h-3.5" /></Link>
    </div>
    <div className="grid grid-cols-2 md:grid-cols-3 gap-2.5 lg:gap-3">
     {GALLERY_GRID.map((src, i) => (
      <div key={i} className={`rounded-2xl overflow-hidden ring-1 ring-[hsla(222,20%,80%,0.5)] group ${i === 0 ? "row-span-2 aspect-auto md:aspect-[3/4]" : "aspect-[4/3]"}`} data-testid={`pbh-gallery-${i}`}>
       <img src={src} alt={`Magic Cruises Sydney Harbour event ${i+1}`} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
      </div>
     ))}
    </div>
   </div>
  </section>

  {/* ───── 6. PACKAGES (compact) ───── */}
  <section id="packages" className="relative py-16 lg:py-20" data-testid="pbh-packages">
   <div className="max-w-7xl mx-auto px-5 lg:px-10">
    <div className="max-w-2xl mb-10">
     <div className="text-pink text-[11px] uppercase tracking-[0.32em] font-black mb-2">Packages</div>
     <h2 className="font-display text-3xl lg:text-4xl text-ink font-black tracking-tight leading-[1.05]">Pick your <span className="italic font-light text-pink">format.</span></h2>
    </div>
    <div className="grid md:grid-cols-3 gap-4 lg:gap-5">
     {PACKAGES.map((p, i) => {
      const t = PACKAGE_THEME[p.accent];
      return (
       <div key={i} className={`relative bg-white rounded-3xl p-6 lg:p-7 ring-1 ${t.ring} hover:-translate-y-1 hover:shadow-xl transition-all overflow-hidden`} data-testid={`pbh-package-${i}`}>
        <div className={`absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r ${t.grad}`} />
        <div className={`text-[10px] uppercase tracking-[0.32em] font-black mb-2 ${t.text}`}>{p.from}</div>
        <h3 className="font-display text-xl lg:text-2xl font-black tracking-tight text-ink leading-tight mb-3">{p.label}</h3>
        <p className="text-sm text-ink-soft leading-relaxed mb-5">{p.desc}</p>
        {p.slug ? (
         <Link to={`/cruises/${p.slug}`} className={`inline-flex items-center gap-2 text-xs font-black uppercase tracking-[0.18em] ${t.text} hover:gap-3 transition-all`}>View package <ArrowRight className="w-3.5 h-3.5" /></Link>
        ) : (
         <a href="#enquiry" className={`inline-flex items-center gap-2 text-xs font-black uppercase tracking-[0.18em] ${t.text} hover:gap-3 transition-all`}>Enquire now <ArrowRight className="w-3.5 h-3.5" /></a>
        )}
       </div>
      );
     })}
    </div>
   </div>
  </section>

  {/* ───── 7. TESTIMONIALS ───── */}
  <section className="relative py-16 lg:py-20 bg-white" data-testid="pbh-testimonials">
   <div className="max-w-7xl mx-auto px-5 lg:px-10">
    <div className="max-w-2xl mb-10">
     <div className="text-pink text-[11px] uppercase tracking-[0.32em] font-black mb-2 flex items-center gap-2">
      <Star className="w-3.5 h-3.5" fill="currentColor" strokeWidth={0} /> Google · 5.0 average · 2,500+ reviews
     </div>
     <h2 className="font-display text-3xl lg:text-4xl text-ink font-black tracking-tight leading-[1.05]">What guests <span className="italic font-light text-pink">say.</span></h2>
    </div>
    <div className="grid md:grid-cols-3 gap-4 lg:gap-5">
     {TESTIMONIALS.slice(0, 3).map((t, i) => (
      <div key={i} className="bg-[hsl(45,100%,97%)] rounded-2xl p-6 ring-1 ring-[hsla(222,20%,80%,0.5)]" data-testid={`pbh-testimonial-${i}`}>
       <div className="flex gap-0.5 mb-3">{Array.from({length: 5}).map((_, j) => <Star key={j} className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />)}</div>
       <p className="text-ink-soft text-[14px] leading-relaxed">"{t.quote.slice(0, 200)}{t.quote.length > 200 && "…"}"</p>
       <div className="mt-5 flex items-center gap-3 pt-4 border-t border-[hsla(222,20%,80%,0.4)]">
        <div className="w-9 h-9 rounded-full bg-gradient-to-br from-pink-500 to-fuchsia-500 text-white flex items-center justify-center font-black text-xs">{t.name.split(" ").map(w => w[0]).slice(0,2).join("")}</div>
        <div>
         <div className="text-xs font-black tracking-tight text-ink">{t.name}</div>
         <div className="text-[10px] uppercase tracking-widest text-ink-mute font-bold">{t.role}</div>
        </div>
       </div>
      </div>
     ))}
    </div>
   </div>
  </section>

  {/* ───── 8. FAQ ───── */}
  <section className="relative py-16 lg:py-20" data-testid="pbh-faq">
   <div className="max-w-3xl mx-auto px-5 lg:px-10">
    <div className="text-center mb-10">
     <div className="text-pink text-[11px] uppercase tracking-[0.32em] font-black mb-2">FAQs</div>
     <h2 className="font-display text-3xl lg:text-4xl text-ink font-black tracking-tight leading-[1.05]">Quick <span className="italic font-light text-pink">answers.</span></h2>
    </div>
    <div className="space-y-2.5">
     {FAQS.map((f, i) => (
      <div key={i} className="bg-white rounded-2xl ring-1 ring-[hsla(222,20%,80%,0.5)] overflow-hidden" data-testid={`pbh-faq-${i}`}>
       <button onClick={() => setOpenFaq(openFaq === i ? -1 : i)} className="w-full flex items-center justify-between gap-4 px-5 py-4 lg:px-6 lg:py-5 text-left hover:bg-[hsl(45,100%,98%)] transition-colors">
        <span className="font-display font-black text-sm lg:text-base tracking-tight text-ink pr-4">{f.q}</span>
        {openFaq === i ? <Minus className="w-4 h-4 shrink-0 text-pink" /> : <Plus className="w-4 h-4 shrink-0 text-pink" />}
       </button>
       {openFaq === i && (
        <div className="px-5 lg:px-6 pb-5 text-sm text-ink-soft leading-relaxed">{f.a}</div>
       )}
      </div>
     ))}
    </div>
   </div>
  </section>

  {/* ───── 9. SINGLE ENQUIRY (only contact form on page) ───── */}
  <section id="enquiry" className="relative overflow-hidden bg-[hsl(225,80%,9%)]" data-testid="pbh-enquiry">
   <div className="absolute inset-0 pointer-events-none" aria-hidden="true, ">
    <div className="absolute -top-32 -left-32 w-[40rem] h-[40rem] rounded-full bg-pink-500/20 blur-3xl" />
    <div className="absolute -bottom-32 -right-32 w-[36rem] h-[36rem] rounded-full bg-amber-400/15 blur-3xl" />
   </div>
   <div className="relative max-w-7xl mx-auto px-5 lg:px-10 py-20 lg:py-28">
    <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-start">
     <div className="lg:col-span-6 text-white">
      <div className="text-amber-300 text-[11px] uppercase tracking-[0.32em] font-black mb-3 flex items-center gap-3">
       <span className="w-8 h-px bg-amber-400" /> Get a quote
      </div>
      <h2 className="font-display font-black tracking-tight leading-[0.95]" style={{ fontSize: "clamp(2rem, 4.5vw, 3.75rem)", letterSpacing: "-0.025em" }}>
       Lock in your private Sydney Harbour cruise.{" "}
       <span className="italic font-light text-amber-300">We reply within the hour.</span>
      </h2>
      <p className="text-white/80 text-base lg:text-lg mt-6 leading-relaxed max-w-xl font-light">
       Tell us your date, guest count and event format. A real human (not a bot) comes back the same day with available dates, a personalised quote and the boarding plan for King St Wharf.
      </p>
      <ul className="grid sm:grid-cols-2 gap-3 mt-8 text-sm font-semibold text-white/90">
       {[
        { Icon: Clock, t: "Reply within 60 minutes" },
        { Icon: Sparkles, t: "Tailored quote · no obligation" },
        { Icon: Users, t: "Up to 120 guests catered" },
        { Icon: Anchor, t: "King St Wharf boarding" }].map((b, i) => {
        const Ico = b.Icon;
        return (
         <li key={i} className="flex items-center gap-3">
          <span className="w-8 h-8 rounded-xl bg-amber-400/15 ring-1 ring-amber-400/40 text-amber-300 flex items-center justify-center shrink-0">
           <Ico className="w-4 h-4" />
          </span>
          {b.t}
         </li>
        );
       })}
      </ul>
      <div className="mt-10 flex flex-wrap items-center gap-5 text-sm pt-8 border-t border-white/10">
       <a href={`tel:${CONTACT.phone1}`} className="inline-flex items-center gap-2 text-white font-black hover:text-amber-300 transition-colors" data-testid="pbh-phone">
        <Phone className="w-4 h-4 text-amber-300" /> {CONTACT.phone1}
       </a>
       <a href={`mailto:${CONTACT.email}`} className="inline-flex items-center gap-2 text-white/85 font-bold hover:text-white transition-colors">
        <GlassWater className="w-4 h-4 text-amber-300" /> {CONTACT.email}
       </a>
       <span className="inline-flex items-center gap-2 text-white/70">
        <MapPin className="w-4 h-4 text-amber-300" /> King St Wharf
       </span>
      </div>
     </div>
     <div className="lg:col-span-6 lg:sticky lg:top-28">
      <div className="rounded-3xl p-1.5 bg-gradient-to-br from-amber-400 via-pink-500 to-fuchsia-600 shadow-[0_40px_90px_-25px_rgba(0,0,0,0.6)]">
       <div className="rounded-[22px] bg-white p-7 lg:p-9">
        <div className="text-pink text-[10px] uppercase tracking-[0.32em] font-black mb-2">Quick enquiry</div>
        <h3 className="font-display text-2xl lg:text-3xl font-black tracking-tight text-ink mb-2 leading-tight">
         Plan your <span className="italic font-light text-pink">private charter.</span>
        </h3>
        <p className="text-sm text-ink-soft mb-5 leading-relaxed">Tell us about your event, we'll handle the rest.</p>
        <EnquiryForm defaultEvent="Private Charter" sourcePage="private-boat-hire-sydney-harbour" compact />
       </div>
      </div>
     </div>
    </div>
   </div>
  </section>

 </main>
 );
}
